import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useLanguage, api } from '../context/AppContext';
import useExerciseHistory from '../hooks/useExerciseHistory';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Zap, Loader2, Play, Dumbbell, Clock, Repeat, Timer, ChevronDown, ChevronUp,
  Battery, BatteryLow, BatteryFull, Star, ThumbsUp, Send, Info,
  AlertTriangle, Shield, Save, BookmarkCheck, ExternalLink, History, RotateCcw, Trophy, Crosshair,
  CalendarDays, Layers, Target, Wrench, HeartPulse, BookOpen, Flame, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';
import PremiumIcon from './PremiumIcon';
import CoachYumixAvatar from './CoachYumixAvatar';
import TempoTooltip from './aiWorkoutGenerator/TempoTooltip';
import AssaultTarget from './aiWorkoutGenerator/AssaultTarget';
import PlanExerciseRow from './aiWorkoutGenerator/PlanExerciseRow';
import FeedbackSection from './aiWorkoutGenerator/FeedbackSection';
import QuickDirectiveChips from './aiWorkoutGenerator/QuickDirectiveChips';
import DraftPlanPreview from './aiWorkoutGenerator/DraftPlanPreview';

const ENERGY_LEVELS = [
  { key: 'low', icon: BatteryLow, label_fr: 'Basse', label_en: 'Low', color: 'text-yellow-400' },
  { key: 'normal', icon: Battery, label_fr: 'Normale', label_en: 'Normal', color: 'text-green-400' },
  { key: 'high', icon: BatteryFull, label_fr: 'Haute', label_en: 'High', color: 'text-primary' },
];

const INTENSITY_LABELS = [
  { value: 1, label_fr: 'Trop facile', icon: '😴', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 2, label_fr: 'Facile', icon: '😊', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  { value: 3, label_fr: 'Parfait', icon: '💪', color: 'bg-primary/20 text-primary border-primary/30' },
  { value: 4, label_fr: 'Difficile', icon: '😤', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
  { value: 5, label_fr: 'Trop dur', icon: '🥵', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

// ============ TEMPO TOOLTIP ============

const EQUIPMENT_OPTIONS = [
  { key: 'gym_complet', label_fr: 'Gym complet', icon: Dumbbell },
  { key: 'poids_du_corps', label_fr: 'Poids du corps', icon: HeartPulse },
  { key: 'halteres', label_fr: 'Halteres', icon: Dumbbell },
  { key: 'elastiques', label_fr: 'Elastiques', icon: Layers },
];

const OBJECTIVE_OPTIONS = [
  { key: 'hypertrophie', label_fr: 'Hypertrophie', desc: 'Gain musculaire', color: 'text-purple-400 border-purple-500/30 bg-purple-500/10' },
  { key: 'force', label_fr: 'Force', desc: 'Puissance max', color: 'text-red-400 border-red-500/30 bg-red-500/10' },
  { key: 'endurance', label_fr: 'Endurance', desc: 'Cardio musculaire', color: 'text-blue-400 border-blue-500/30 bg-blue-500/10' },
  { key: 'réhabilitation', label_fr: 'Réhabilitation', desc: 'Mobilité / Soin', color: 'text-teal-400 border-teal-500/30 bg-teal-500/10' },
];

// ============ MAIN AI GENERATOR ============
export default function AIWorkoutGenerator({ clientId = null, onPlanGenerated = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [energy, setEnergy] = useState('normal');
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [injuryStatus, setInjuryStatus] = useState(null);
  const [planInjuryContext, setPlanInjuryContext] = useState(null);
  const [planSaved, setPlanSaved] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  // New elite parameters
  const [daysPerWeek, setDaysPerWeek] = useState(4);
  const [maxDuration, setMaxDuration] = useState(45);
  const [includeSupersets, setIncludeSupersets] = useState(false);
  const [equipment, setEquipment] = useState('gym_complet');
  const [objective, setObjective] = useState('');
  const [limitations, setLimitations] = useState('');
  // Iter. 406.fr.26 — Yuri request: zone libre "Préférences & exercices
  // souhaités/exclus" en plus des Blessures. Le coach peut écrire :
  //   "Pas de push-ups, pas de burpees, ajouter des fentes bulgares,
  //    favoriser le tirage poulie sur les tractions, etc."
  // Envoyé au LLM via `coach_directives` (concaténé avec un préfixe).
  const [customPreferences, setCustomPreferences] = useState('');
  const [intensityLevel, setIntensityLevel] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [progressionStatus, setProgressionStatus] = useState(null);
  // Template save state
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const isCoach = user?.role === 'coach' || user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    api.get('/injuries').then(res => {
      setInjuryStatus(res.data);
    }).catch(() => {});
    // Fetch auto-progression status
    api.get('/workouts/progression-status').then(res => {
      const data = res.data;
      setProgressionStatus(data);
      // Auto-apply recommended intensity level
      if (data.has_progression && data.recommendation?.next_intensity) {
        setIntensityLevel(data.recommendation.next_intensity);
      }
    }).catch(() => {});
  }, []);

  const generate = async () => {
    if (!user?.user_id && !clientId) {
      toast.error(language === 'fr' ? 'Session expirée — reconnecte-toi.' : 'Session expired — sign in again.');
      return;
    }
    setGenerating(true);
    setPlan(null);
    setPlanInjuryContext(null);
    setPlanSaved(false);
    try {
      // Iter. 406.fh — IA Elite now hits the SAME Armurerie engine that
      // produces real MULTI-WEEK plans (`weeks[].days[].exercises[]`). This
      // unifies the Coach YUMIX Elite, the Wizard onboarding and the J42
      // reassessment on a single proven generator + storage shape, so
      // MyPlanTab renders the result instantly. The legacy single-session
      // /workouts/generate endpoint is retained for live "Séance Express"
      // generation but is no longer used here.
      const targetId = clientId || user.user_id;

      const OBJECTIVE_TO_FOCUS = {
        hypertrophie: 'general',
        force: 'upper',
        endurance: 'cardio',
        'réhabilitation': 'general',
        rehabilitation: 'general',
      };
      const equipmentMode = equipment === 'gym_complet' ? 'gym' : 'home';
      const equipmentDetail = {
        poids_du_corps: 'Poids du corps uniquement',
        elastiques: 'Bandes élastiques uniquement',
        halteres: 'Haltères de base + poids du corps',
        gym_complet: 'Gym complet (barres, haltères, machines, câbles, bancs)',
      }[equipment] || '';
      const energyLabel = { low: 'basse', normal: 'normale', high: 'haute' }[energy] || 'normale';

      const directiveParts = [
        `Niveau visé: ${intensityLevel || 'intermédiaire'}`,
        `Objectif principal: ${objective || 'hypertrophie'}`,
        `Énergie ressentie: ${energyLabel}`,
        `Durée max par séance: ${maxDuration} minutes`,
        equipmentDetail ? `Équipement précis: ${equipmentDetail}` : '',
        includeSupersets ? 'Inclure des supersets antagonistes (format 1A/1B).' : '',
        (limitations || '').trim() ? `Blessures/limitations à respecter strictement: ${limitations.trim()}` : '',
        (customPreferences || '').trim() ? `Préférences du coach/client à PRENDRE EN COMPTE (exercices à exclure, à inclure, variantes préférées, etc.): ${customPreferences.trim()}` : '',
      ].filter(Boolean);

      const genPayload = {
        focus: OBJECTIVE_TO_FOCUS[objective] || 'general',
        days_per_week: daysPerWeek,
        // Iter. 406.fr.28 — Cycle YUMIX = 6 semaines (DEFAULT_CYCLE_DAYS / 7).
        duration_weeks: 6,
        equipment: equipmentMode,
        notes: clientId
          ? 'IA Elite (Coach) — plan personnalisé pour le client sélectionné.'
          : 'IA Elite — plan personnel auto-généré.',
        coach_directives: directiveParts.join(' | '),
        // Iter. 406.fr.29 — Yuri bug : le toggle Super Sets ne déclenchait
        // aucun groupement 1A/1B parce que c'était passé uniquement dans
        // les directives texte. Le backend lit maintenant ce flag explicite
        // pour injecter le schéma `superset_group` dans le JSON template
        // du LLM.
        include_supersets: includeSupersets,
      };

      const res = await api.post(`/coach/command/armurerie/${targetId}/generate`, genPayload);

      // Iter. 406.fr.28 — BETA usage tracker (fire-and-forget). Never blocks
      // the user flow on log failure. Powers `/analytics/ai-elite-usage/stats`
      // so we know how many clients self-generate before monetizing.
      api.post('/analytics/ai-elite-usage', {
        event_type: 'generate',
        client_id: clientId || null,
        focus: genPayload.focus,
        duration_weeks: genPayload.duration_weeks,
        days_per_week: genPayload.days_per_week,
        equipment: genPayload.equipment,
        success: !!(res.data?.success !== false && res.data?.draft_plan),
      }).catch(() => { /* tracker must never break the UX */ });

      // Iter. 406.fi — Inline-handle the new "success: false / empty_plan_*" 
      // contract from the generate endpoint (validation may drop all exos).
      if (res.data?.success === false || !res.data?.draft_plan) {
        const code = res.data?.error_code;
        const isEmpty = code === 'empty_plan_after_validation' || code === 'empty_plan_rejected';
        const msg = isEmpty
          ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
          : (res.data?.error || 'Erreur génération');
        toast.error(msg, {
          duration: 10000,
          action: isEmpty ? { label: 'Réessayer', onClick: () => { generate(); } } : undefined,
        });
        return;
      }

      // Stash the draft for the save step. We DON'T deploy here — the Coach
      // still gets to review the plan and explicitly click "Activate". This
      // preserves the original IA Elite UX of "generate then approve".
      setPlan(res.data.draft_plan);
      setPlanId(res.data.draft_plan?.plan_id || `draft_${Date.now()}`);
      setPlanInjuryContext(null);
      setShowFeedback(false);
      // Iter. 406.fi — Defer the parent callback to next tick so the
      // experimental `react-hooks/purity` rule sees a clean event-driven
      // call (not a possibly-render-time prop invocation). Equivalent
      // observable behaviour for the consumer.
      const fwd = onPlanGenerated;
      if (fwd) setTimeout(() => fwd(res.data.draft_plan), 0);
      toast.success(t('ai_workout_gen.seance_generee', 'Plan généré !'));
    } catch (err) {
      const data = err.response?.data;
      const detailObj = data?.detail;
      const detail = detailObj || data?.error || err.message || 'Erreur génération';
      const code = (typeof detailObj === 'object' && detailObj?.error_code) || data?.error_code;
      const isEmptyReject = code === 'empty_plan_rejected' || code === 'empty_plan_after_validation';
      console.error('[AIWorkoutGenerator] generation failure', { code, detail, raw: data });
      const msg = isEmptyReject
        ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
        : (typeof detail === 'string' ? detail : (detail?.error || JSON.stringify(detail)));
      toast.error(msg, {
        duration: 10000,
        action: isEmptyReject ? { label: 'Réessayer', onClick: () => { generate(); } } : undefined,
      });
    } finally {
      setGenerating(false);
    }
  };

  const savePlan = async () => {
    if (!plan) return;
    setSavingPlan(true);
    try {
      // Iter. 406.fh — Activation now goes through the SAME Armurerie deploy
      // route the "Mon Plan" button uses. The backend stores `weeks` at the
      // top level of `training_plans`, so MyPlanTab renders the new plan
      // INSTANTLY — no more "saved" toast on a plan that never appears.
      const targetId = clientId || user.user_id;
      const res = await api.post(`/coach/command/armurerie/${targetId}/deploy`, {
        plan,
        // Iter. 406.fr.28 — Default cycle = 6 sem.
        duration_weeks: plan.duration_weeks || 6,
      });
      if (!res.data?.success) {
        throw new Error(res.data?.message || 'Deploy failed');
      }
      setPlanSaved(true);
      toast.success(t('ai_workout_gen.plan_active', 'Plan activé. Ton ancien plan est archivé.'));
      // Iter. 406.fr.28 — Tracker (fire-and-forget): a deploy is the strongest
      // signal that the BETA feature is producing real value.
      api.post('/analytics/ai-elite-usage', {
        event_type: 'deploy',
        client_id: clientId || null,
        duration_weeks: plan?.duration_weeks || 6,
        success: true,
      }).catch(() => { /* never break the UX */ });
      // Cache invalidation — any mounted MyPlanTab refetches instantly.
      try { window.dispatchEvent(new CustomEvent('yumix:plan-activated', { detail: { plan_id: res.data.plan_id, client_id: targetId } })); } catch { /* ignore */ }
      // Force redirect so the user IMMEDIATELY sees the new active plan
      // (was: stuck on the generator screen, looking like nothing happened).
      try { navigate('/workouts?tab=plan'); } catch { /* no-op */ }
      return res.data;
    } catch (err) {
      const data = err.response?.data;
      const detailObj = data?.detail;
      const detail = detailObj || err.message;
      const code = (typeof detailObj === 'object' && detailObj?.error_code) || data?.error_code;
      const isEmptyReject = code === 'empty_plan_rejected' || code === 'empty_plan_after_validation';
      // Iter. 406.fi — Atomic safety-net rejection: the existing plan is
      // intact. Tell the coach explicitly + offer one-click regenerate.
      const msg = isEmptyReject
        ? 'Échec de génération — ton plan actuel a été conservé. Réessaie ou ajuste ton équipement.'
        : (typeof detail === 'string' ? detail : (detail?.error || 'Erreur activation'));
      toast.error(msg, {
        duration: 10000,
        action: isEmptyReject ? { label: 'Réessayer', onClick: () => { generate(); } } : undefined,
      });
    } finally {
      setSavingPlan(false);
    }
  };

  const saveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await api.post('/coach/command/templates', {
        name: templateName.trim(),
        description: templateDesc.trim(),
        params: {
          days_per_week: daysPerWeek,
          max_duration_minutes: maxDuration,
          equipment,
          objective,
          include_supersets: includeSupersets,
          energy_level: energy,
        },
      });
      toast.success(t('ai_workout_gen.template_sauvegarde_dans_ta_bibliotheque', 'Template sauvegarde dans ta bibliotheque!'));
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateDesc('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ai-workout-generator">
      {/* ─── BETA BANNER ─── */}
      {!isCoach && (
        <div className="p-3 rounded-xl border-2 border-amber-500/40 bg-amber-500/5" data-testid="beta-banner">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-heading uppercase tracking-wider text-amber-400 font-bold">
                {t('ai_workout_gen.mode_experimental_beta', 'Mode Experimental Beta')}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {t('ai_workout_gen.testez_le_generateur_ia_les_plans_offici', 'Testez le generateur IA. Les plans officiels restent sous la supervision du Coach.')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Generator Controls */}
      <Card className="bg-gradient-to-br from-primary/10 to-orange-500/10 border-primary/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
            <PremiumIcon glyph="⚡" tone="orange" size="xs" />
            {t('ai_workout_gen.generateur_ia_elite', 'Plan Élite YUMIX')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* ─── AUTO-PROGRESSION BANNER ─── */}
          {progressionStatus?.has_progression && progressionStatus.recommendation && (
            <div
              className={`p-3 rounded-xl border ${
                progressionStatus.recommendation.action === 'deload'
                  ? 'border-blue-500/30 bg-blue-500/5'
                  : progressionStatus.recommendation.action === 'force_upgrade'
                  ? 'border-red-500/40 bg-red-500/10'
                  : progressionStatus.recommendation.action?.includes('increase') || progressionStatus.recommendation.action === 'upgrade_intensity'
                  ? 'border-emerald-500/30 bg-emerald-500/5'
                  : 'border-primary/20 bg-primary/5'
              }`}
              data-testid="progression-banner"
            >
              <div className="flex items-start gap-2.5">
                <TrendingUp className={`w-5 h-5 shrink-0 mt-0.5 ${
                  progressionStatus.recommendation.action === 'deload' ? 'text-blue-400' :
                  progressionStatus.recommendation.action === 'force_upgrade' ? 'text-red-400' :
                  'text-emerald-400'
                }`} />
                <div>
                  <p className={`text-xs font-heading uppercase tracking-wider font-bold ${
                    progressionStatus.recommendation.action === 'deload' ? 'text-blue-400' :
                    progressionStatus.recommendation.action === 'force_upgrade' ? 'text-red-400' :
                    'text-emerald-400'
                  }`}>
                    {progressionStatus.recommendation.label_fr}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {progressionStatus.recommendation.detail_fr}
                  </p>
                  <p className="text-[9px] text-muted-foreground mt-1 italic">
                    {t('ai_workout_gen.last_rating_summary', 'Dernière note : {{rating}}/5 | {{count}} plan(s) complété(s)', { rating: progressionStatus.last_rating, count: progressionStatus.total_plans_completed })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ─── ROW 1: Days + Duration ─── */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {t('ai_workout_gen.jours_semaine', 'Jours / semaine')}
              </label>
              <div className="flex items-center gap-1">
                {[2, 3, 4, 5, 6].map(d => (
                  <button
                    key={d}
                    onClick={() => setDaysPerWeek(d)}
                    data-testid={`days-${d}`}
                    className={`flex-1 h-9 rounded-lg text-xs font-bold transition-all border ${
                      daysPerWeek === d
                        ? 'bg-red-500/20 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                        : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {t('ai_workout_gen.duree_max_min', 'Duree max (min)')}
              </label>
              <div className="flex items-center gap-1">
                {[20, 35, 45, 60].map(t => (
                  <button
                    key={t}
                    onClick={() => setMaxDuration(t)}
                    data-testid={`duration-${t}`}
                    className={`flex-1 h-9 rounded-lg text-xs font-bold transition-all border ${
                      maxDuration === t
                        ? 'bg-red-500/20 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                        : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/30'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ─── ROW 2: Objective ─── */}
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Target className="w-3 h-3" />
              {t('ai_workout_gen.objectif_principal', 'Objectif principal')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {OBJECTIVE_OPTIONS.map(o => (
                <button
                  key={o.key}
                  onClick={() => setObjective(objective === o.key ? '' : o.key)}
                  data-testid={`objective-${o.key}`}
                  className={`p-2 rounded-lg border text-center transition-all ${
                    objective === o.key
                      ? o.color + ' border-2 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                      : 'bg-secondary/30 border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  <span className="text-xs font-bold block">{o.label_fr}</span>
                  <span className="text-[9px] block mt-0.5 opacity-70">{o.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ─── ROW 3: Energy Level ─── */}
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 block">
              {t('ai_workout_gen.niveau_d_energie', 'Niveau d\\\'énergie')}
            </label>
            <div className="flex gap-2">
              {ENERGY_LEVELS.map(e => (
                <button
                  key={e.key}
                  onClick={() => setEnergy(e.key)}
                  data-testid={`energy-${e.key}`}
                  className={`flex-1 flex items-center justify-center gap-1.5 p-2 rounded-lg border transition-all ${
                    energy === e.key
                      ? 'bg-red-500/20 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                      : 'bg-secondary/50 border-border text-muted-foreground'
                  }`}
                >
                  <e.icon className="w-4 h-4" />
                  {/* Iter. 406.ff — wrong key (`equip_${e.id}` → `e.id`
                      is undefined, key resolved to literal
                      `ai_workout_gen.equip_na`). Fix: use `e.label_fr` /
                      `e.label_en` directly since the array already carries
                      bilingual labels — no i18n indirection needed. */}
                  <span className="text-xs font-bold">{language === 'en' ? (e.label_en || e.label_fr) : e.label_fr}</span>
                </button>
              ))}
            </div>
          </div>

          {/* ─── ROW 4: Equipment ─── */}
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Wrench className="w-3 h-3" />
              {t('ai_workout_gen.equipement_disponible', 'Équipement disponible')}
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {EQUIPMENT_OPTIONS.map(eq => (
                <button
                  key={eq.key}
                  onClick={() => setEquipment(eq.key)}
                  data-testid={`equipment-${eq.key}`}
                  className={`flex items-center justify-center gap-1.5 p-2 rounded-lg border transition-all text-xs font-bold ${
                    equipment === eq.key
                      ? 'bg-red-500/20 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                      : 'bg-secondary/50 border-border text-muted-foreground hover:border-primary/30'
                  }`}
                >
                  <eq.icon className="w-3.5 h-3.5" />
                  {eq.label_fr}
                </button>
              ))}
            </div>
          </div>

          {/* ─── ROW 5: Intensity Level ─── */}
          <div>
            <label className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Flame className="w-3 h-3" />
              {t('ai_workout_gen.niveau_d_intensite', 'Niveau d\\\'intensite')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { key: '', label_fr: 'Standard', color: 'border-border' },
                { key: 'intermediaire', label_fr: 'Intermediaire', color: 'border-blue-500/40' },
                { key: 'avance', label_fr: 'Avance', color: 'border-red-500/40' },
              ].map(lvl => (
                <button
                  key={lvl.key}
                  onClick={() => setIntensityLevel(lvl.key)}
                  data-testid={`intensity-${lvl.key || 'standard'}`}
                  className={`p-2 rounded-lg border transition-all text-xs font-bold ${
                    intensityLevel === lvl.key
                      ? lvl.key === 'avance'
                        ? 'bg-red-500/25 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                        : lvl.key === 'intermediaire'
                        ? 'bg-blue-500/20 border-blue-500/70 text-blue-300 ring-2 ring-blue-500/50 shadow-[0_0_14px_rgba(59,130,246,0.45)]'
                        : 'bg-red-500/20 border-red-500/70 text-red-300 ring-2 ring-red-500/50 shadow-[0_0_14px_rgba(239,68,68,0.55)]'
                      : `bg-secondary/50 ${lvl.color} text-muted-foreground hover:border-primary/30`
                  }`}
                >
                  {lvl.label_fr}
                </button>
              ))}
            </div>
            {intensityLevel === 'avance' && (
              <p className="text-[9px] text-red-400 mt-1 italic">
                {t('ai_workout_gen.rir_tempo_tut_drop_sets_rest_pause_surch', 'RIR, tempo TUT, drop sets, rest-pause — surcharge progressive elite')}
              </p>
            )}
          </div>

          {/* ─── ROW 6: Super Sets toggle + Advanced toggle ─── */}
          <div className="flex items-center gap-4">
            <label
              className={`flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-lg border transition-all ${
                includeSupersets
                  ? 'bg-red-500/15 border-red-500/70 text-red-300 ring-2 ring-red-500/40 shadow-[0_0_12px_rgba(239,68,68,0.45)]'
                  : 'bg-secondary/30 border-border text-muted-foreground hover:border-primary/30'
              }`}
              data-testid="superset-toggle"
            >
              <input
                type="checkbox"
                checked={includeSupersets}
                onChange={(e) => setIncludeSupersets(e.target.checked)}
                className="sr-only"
              />
              <Layers className="w-4 h-4" />
              <span className="text-xs font-bold">Super Sets (1A/1B)</span>
              <div className={`w-8 h-4 rounded-full transition-all relative ${includeSupersets ? 'bg-orange-500' : 'bg-zinc-600'}`}>
                <div className={`w-3 h-3 rounded-full bg-white absolute top-0.5 transition-all ${includeSupersets ? 'left-4' : 'left-0.5'}`} />
              </div>
            </label>

            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 transition-all text-[10px] font-bold uppercase tracking-wider ${
                showAdvanced || ((limitations || '').trim() || (customPreferences || '').trim())
                  ? 'bg-amber-500/10 border-amber-500/60 text-amber-300 ring-1 ring-amber-500/30 shadow-[0_0_10px_rgba(245,158,11,0.35)]'
                  : 'bg-secondary/40 border-amber-500/40 text-amber-200/80 hover:border-amber-500/60 hover:bg-amber-500/10'
              }`}
              data-testid="toggle-advanced"
            >
              <Shield className="w-3.5 h-3.5" />
              {showAdvanced ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {t('ai_workout_gen.blessures_et_preferences', 'Blessures & Préférences')}
              {((limitations || '').trim() || (customPreferences || '').trim()) && (
                <span className="ml-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" data-testid="advanced-active-dot" />
              )}
            </button>
          </div>

          {/* ─── ADVANCED: Limitations ─── */}
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2"
            >
              {injuryStatus && injuryStatus.total_active > 0 && (
                <div className={`p-2.5 rounded-lg border flex items-center gap-2 ${
                  injuryStatus.recovery_mode
                    ? 'bg-red-500/10 border-red-500/30'
                    : 'bg-orange-500/10 border-orange-500/30'
                }`} data-testid="injury-status-indicator">
                  {injuryStatus.recovery_mode ? (
                    <>
                      <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
                      <span className="text-[10px] text-red-300 font-bold uppercase">
                        {t('ai_workout_gen.mode_recuperation_mobilite_uniquement', 'Mode Recuperation — Mobilité uniquement')}
                      </span>
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4 text-orange-400 shrink-0" />
                      <span className="text-[10px] text-orange-300">
                        {t('ai_workout_gen.injured_zones_count', '{{count}} zone(s) blessée(s) — Substitution auto activée', { count: injuryStatus.total_active })}
                      </span>
                    </>
                  )}
                </div>
              )}
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold text-red-300/90 mb-1 flex items-center gap-1.5">
                  <Shield className="w-3 h-3" />
                  {t('ai_workout_gen.blessures_limitations', 'Blessures & limitations')}
                </p>
                <Textarea
                  value={limitations}
                  onChange={(e) => setLimitations(e.target.value)}
                  placeholder={t('ai_workout_gen.ex_blessure_genou_droit_condition_cardia', 'Ex: Blessure genou droit, condition cardiaque, éviter squats profonds...')}
                  className="min-h-[50px] text-sm bg-secondary border-input resize-none"
                  data-testid="limitations-input"
                />
                <p className="text-[9px] text-muted-foreground italic mt-1">
                  {t('ai_workout_gen.l_ia_exclura_automatiquement_les_exercic', 'L\'IA exclura automatiquement les exercices a risque et adaptera l\'intensite.')}
                </p>
              </div>

              {/* Iter. 406.fr.26 — Préférences libres (Yuri request) */}
              <div className="pt-1">
                <p className="text-[10px] uppercase tracking-widest font-bold text-cyan-300/90 mb-1 flex items-center gap-1.5">
                  <Wrench className="w-3 h-3" />
                  {t('ai_workout_gen.preferences_directives', 'Préférences & directives')}
                </p>

                {/* Iter. 406.fr.27 — Chips de directives rapides (1-tap).
                    Mécanique toggle : tap = ajoute la phrase à la textarea,
                    re-tap = retire la phrase. Le LLM reçoit le texte final
                    via `customPreferences` → `coach_directives`. */}
                <QuickDirectiveChips
                  value={customPreferences}
                  onChange={setCustomPreferences}
                  language={language}
                />

                <Textarea
                  value={customPreferences}
                  onChange={(e) => setCustomPreferences(e.target.value)}
                  placeholder={t(
                    'ai_workout_gen.ex_pas_de_pushups',
                    'Ex: Pas de push-ups ni de burpees. Ajoute des fentes bulgares. Favorise tirage poulie au lieu de tractions. Inclure du sprint sur tapis 5 min.'
                  )}
                  className="min-h-[70px] text-sm bg-secondary border-input resize-none"
                  data-testid="custom-preferences-input"
                />
                <p className="text-[9px] text-muted-foreground italic mt-1">
                  {t(
                    'ai_workout_gen.preferences_hint',
                    'Décris ce que tu veux ou ne veux pas : exercices à exclure, à ajouter, variantes préférées, focus sur certains muscles… Coach YUMIX en tiendra compte.'
                  )}
                </p>
              </div>
            </motion.div>
          )}

          {/* Generate Button */}
          <Button
            onClick={generate}
            disabled={generating}
            className="w-full bg-primary text-primary-foreground font-heading uppercase text-sm h-11"
            data-testid="generate-workout-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                {t('ai_workout_gen.generation_en_cours', 'Generation en cours...')}
              </>
            ) : (
              <>
                <Zap className="w-5 h-5 mr-2" />
                {t('ai_workout_gen.generate_min_btn', 'Générer ({{min}} min)', { min: maxDuration })}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* H — Coach YUMIX Hero Loading Screen (during AI generation) */}
      {generating && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          className="relative overflow-hidden rounded-2xl border border-orange-400/40 bg-gradient-to-br from-orange-950/40 via-card/70 to-red-950/40 p-5"
          style={{ boxShadow: '0 0 28px rgba(251,146,60,0.30), inset 0 0 0 1px rgba(251,146,60,0.18)' }}
          data-testid="coach-yumix-generation-loader"
        >
          <div className="flex items-center gap-4">
            <CoachYumixAvatar variant="hero" size="lg" glowColor="orange" />
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-orange-300 mb-1">
                {language === 'fr' ? 'Coach YUMIX' : 'Coach YUMIX'}
              </p>
              <p className="font-heading text-base sm:text-lg uppercase tracking-tight text-foreground leading-tight">
                {language === 'fr' ? "Je conçois ta séance…" : 'Crafting your session…'}
              </p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-yumix-pulse" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-yumix-pulse" style={{ animationDelay: '160ms' }} />
                <span className="w-2 h-2 rounded-full bg-orange-400 animate-yumix-pulse" style={{ animationDelay: '320ms' }} />
                <span className="ml-2 text-[11px] text-muted-foreground">
                  {language === 'fr' ? 'Sélection des exercices…' : 'Selecting exercises…'}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Generated Plan */}
      {plan && (
        <Card className="bg-card border-border" data-testid="generated-plan">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading uppercase tracking-wider flex items-center gap-2">
                <Dumbbell className="w-4 h-4 text-orange-400" />
                {plan.plan_name || (t('ai_workout_gen.seance_generee_fzb1', 'Séance Generee'))}
              </CardTitle>
              <div className="flex items-center gap-2">
                {planInjuryContext?.recovery_mode && (
                  <span className="text-[9px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full font-bold uppercase" data-testid="plan-recovery-label">
                    {t('ai_workout_gen.recuperation', 'Recuperation')}
                  </span>
                )}
                {planInjuryContext?.has_injuries && !planInjuryContext?.recovery_mode && (
                  <span className="text-[9px] bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full font-bold uppercase" data-testid="plan-adapted-label">
                    {t('ai_workout_gen.adaptee', 'Adaptee')}
                  </span>
                )}
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {plan.duration_minutes || 45} min
                </span>
                <button onClick={() => setExpanded(!expanded)} className="p-1">
                  {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
              </div>
            </div>
          </CardHeader>

          {expanded && (
            <CardContent className="space-y-4">
              {/* Iter. 406.fr.25 — Render the actual multi-week structure
                  returned by /armurerie/.../generate. Falls back to the
                  legacy session-shape (warmup/main_workout/cooldown) for
                  any older code path that may still return it. */}
              {Array.isArray(plan.weeks) && plan.weeks.length > 0 ? (
                <DraftPlanPreview
                  plan={plan}
                  onMutate={setPlan}
                  language={language}
                />
              ) : (
                <>
                  {/* Legacy Warmup */}
                  {plan.warmup?.length > 0 && (
                    <div>
                      <p className="text-xs font-heading uppercase tracking-wider text-yellow-400 mb-2 flex items-center gap-1.5">
                        <Timer className="w-3.5 h-3.5" /> {t('ai_workout_gen.echauffement', 'Echauffement')}
                      </p>
                      <div className="space-y-2">
                        {plan.warmup.map((ex, i) => (
                          <PlanExerciseRow key={i} exercise={ex} index={i} language={language} planId={planId} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy Main Workout — with Tempo info tooltip + Super Set support */}
                  {plan.main_workout?.length > 0 && (
                    <div>
                      <p className="text-xs font-heading uppercase tracking-wider text-primary mb-2 flex items-center gap-1.5">
                        <Dumbbell className="w-3.5 h-3.5" />
                        {t('ai_workout_gen.corps_de_seance', 'Corps de séance')}
                        <TempoTooltip language={language} />
                      </p>
                      <div className="space-y-2">
                        {plan.main_workout.map((ex, i) => {
                          const ssGroup = ex.superset_group;
                          const prevSsGroup = i > 0 ? plan.main_workout[i - 1]?.superset_group : null;
                          const isNewSupersetPair = ssGroup && ssGroup !== prevSsGroup && ssGroup.endsWith('A');
                          return (
                            <React.Fragment key={i}>
                              {isNewSupersetPair && (
                                <div className="flex items-center gap-2 pt-1">
                                  <Layers className="w-3 h-3 text-orange-400" />
                                  <span className="text-[9px] text-orange-400 font-bold uppercase tracking-wider">
                                    Super Set {ssGroup.replace('A', '')}
                                  </span>
                                  <div className="flex-1 h-px bg-orange-500/20" />
                                </div>
                              )}
                              <div className={ssGroup ? 'pl-2 border-l-2 border-orange-500/30' : ''}>
                                {ssGroup && (
                                  <span className="text-[9px] font-black text-orange-400 mb-0.5 block">{ssGroup}</span>
                                )}
                                <PlanExerciseRow exercise={ex} index={i} language={language} planId={planId} />
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Legacy Cooldown */}
                  {plan.cooldown?.length > 0 && (
                    <div>
                      <p className="text-xs font-heading uppercase tracking-wider text-cyan-400 mb-2 flex items-center gap-1.5">
                        <Star className="w-3.5 h-3.5" /> {t('ai_workout_gen.retour_au_calme', 'Retour au calme')}
                      </p>
                      <div className="space-y-2">
                        {plan.cooldown.map((ex, i) => (
                          <PlanExerciseRow key={i} exercise={ex} index={i} language={language} planId={planId} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Coach Tip */}
              {plan.coach_tip && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <p className="text-xs text-primary italic">&ldquo;{plan.coach_tip}&rdquo;</p>
                </div>
              )}

              {/* ─── SAVE PLAN BUTTON ─── */}
              <Button
                onClick={savePlan}
                disabled={savingPlan || planSaved}
                variant={planSaved ? "outline" : "default"}
                className={`w-full font-heading uppercase text-sm ${
                  planSaved ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' : 'bg-amber-600 hover:bg-amber-500 text-white'
                }`}
                data-testid="save-plan-btn"
              >
                {savingPlan ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : planSaved ? (
                  <BookmarkCheck className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {planSaved
                  ? (t('ai_workout_gen.plan_sauvegarde', 'Plan sauvegarde'))
                  : (t('ai_workout_gen.sauvegarder_ce_plan', 'Sauvegarder ce plan'))
                }
              </Button>

              {/* ─── SAVE AS TEMPLATE (Coach only) ─── */}
              {isCoach && !showSaveTemplate && (
                <Button
                  onClick={() => setShowSaveTemplate(true)}
                  variant="outline"
                  className="w-full font-heading uppercase text-xs border-primary/30 text-primary hover:bg-primary/10"
                  data-testid="save-as-template-btn"
                >
                  <BookOpen className="w-4 h-4 mr-1.5" />
                  {t('ai_workout_gen.sauvegarder_comme_template', 'Sauvegarder comme Template')}
                </Button>
              )}
              {isCoach && showSaveTemplate && (
                <div className="p-3 rounded-lg border border-primary/30 bg-primary/5 space-y-2" data-testid="save-template-form">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-bold">
                    {t('ai_workout_gen.nouveau_template', 'Nouveau Template')}
                  </p>
                  <Input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={t('ai_workout_gen.nom_du_template_ex_programme_debutant_ma', 'Nom du template (ex: Programme Debutant Maison)')}
                    className="text-sm"
                    data-testid="template-name-input"
                  />
                  <Textarea
                    value={templateDesc}
                    onChange={(e) => setTemplateDesc(e.target.value)}
                    placeholder={t('ai_workout_gen.description_courte_optionnel', 'Description courte (optionnel)')}
                    className="min-h-[40px] text-xs resize-none"
                    data-testid="template-desc-input"
                  />
                  <p className="text-[9px] text-muted-foreground">
                    {t('ai_workout_gen.template_params_summary', 'Paramètres : {{days}}j/sem, {{min}}min, {{obj}}, {{eq}}', { days: daysPerWeek, min: maxDuration, obj: objective || t('common.general', 'Général'), eq: equipment })}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowSaveTemplate(false)} className="flex-1" data-testid="template-cancel-btn">
                      {t('ai_workout_gen.annuler', 'Annuler')}
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !templateName.trim()}
                      className="flex-1 bg-primary text-primary-foreground"
                      data-testid="template-save-btn"
                    >
                      {savingTemplate ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <BookOpen className="w-3 h-3 mr-1" />}
                      {t('ai_workout_gen.sauvegarder', 'Sauvegarder')}
                    </Button>
                  </div>
                </div>
              )}

              {/* Feedback Toggle */}
              {!showFeedback ? (
                <Button
                  onClick={() => setShowFeedback(true)}
                  variant="outline"
                  className="w-full font-heading uppercase text-xs"
                  data-testid="show-feedback-btn"
                >
                  <ThumbsUp className="w-4 h-4 mr-1.5" />
                  {t('ai_workout_gen.evaluer_l_intensite', 'Evaluer l\\\'intensite')}
                </Button>
              ) : (
                <FeedbackSection
                  planId={planId}
                  language={language}
                  onSubmitted={() => setShowFeedback(false)}
                />
              )}
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
}
